import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';

/**
 * The header's agent light ("tinycms UX optimization review v6", Prototype v6):
 * green "Agent working", pulsing, while the site's agent is using its tools (a
 * call in the last few minutes); a quiet grey dot, with when it was last
 * active, once it has been used; and for a site no agent has ever connected
 * to, a pulsing red "Agent connection not found" that opens Connections.
 */

export const AGENT_ACTIVITY = gql`
  query AgentActivity($accountId: ID!) {
    agentActivity(accountId: $accountId) {
      lastCallAt
      callsLastHour
      connected
    }
  }
`;

const WORKING_MS = 5 * 60 * 1000;

const ago = (ms: number) => {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

export function AgentStatus() {
  const { accountId } = useAccount();
  const { data } = useQuery<{
    agentActivity: { lastCallAt?: string | null; callsLastHour: number; connected: boolean };
  }>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId,
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });
  if (!data) return null; // nothing until we know, so it never flashes "not found"
  if (!data.agentActivity.connected) {
    const label = 'Agent connection not found';
    return (
      <Link
        to="/settings?section=integrations"
        className="ink-agent"
        data-state="missing"
        title="No agent has connected to this site yet. Open Connections to set one up."
        aria-label={label}
        style={{ textDecoration: 'none' }}
      >
        <span className="ink-agent-dot" />
        <span className="ink-agent-label">{label}</span>
      </Link>
    );
  }
  const last = data.agentActivity.lastCallAt ? new Date(data.agentActivity.lastCallAt) : null;
  if (!last || Number.isNaN(last.getTime())) return null;
  const since = Date.now() - last.getTime();
  const working = since < WORKING_MS;
  const label = working ? 'Agent working' : `Agent last active ${ago(since)}`;
  return (
    <span
      className="ink-agent"
      data-working={working ? 'true' : 'false'}
      title={label}
      role="status"
      aria-label={label}
    >
      <span className="ink-agent-dot" />
      {working ? <span className="ink-agent-label">Agent working</span> : null}
    </span>
  );
}
